import { HugeiconsIcon } from '@hugeicons/react'
import { Alert01Icon, Cancel01Icon } from '@hugeicons/core-free-icons'
import { useBrowserSupport } from '@/hooks/useBrowserSupport'

/**
 * Non-blocking, dismissible browser support notice.
 *
 * Renders a fixed stripe (always visible during scroll) plus a same-height
 * flow spacer so underlying content is never obscured. Dismissed state persists
 * in localStorage for 30 days and is shared reactively across all hook consumers
 * (e.g. the sidebar offset disappears the moment this banner is closed).
 * Returns null for all supported browsers — zero impact on the happy path.
 */
export function BrowserSupportBanner() {
  const { isBannerVisible, bannerHeight, browser, dismiss } = useBrowserSupport()

  if (!isBannerVisible) return null

  const browserName = browser?.label ?? 'This browser'

  return (
    <>
      {/* Flow spacer — reserves the banner's height so content renders below the fixed stripe */}
      <div aria-hidden="true" style={{ height: bannerHeight }} className="w-full shrink-0" />

      {/* Fixed stripe — stays at the top of the viewport during scroll */}
      <div
        className="fixed top-0 inset-x-0 z-[9999] flex items-center gap-3 px-4 py-2.5 border-b bg-warning-tint dark:bg-warning-tint border-warning/30 dark:border-warning/30"
        role="region"
        aria-label="Browser compatibility notice"
      >
        <div className="flex-shrink-0 p-1.5 rounded-full bg-warning-tint dark:bg-warning-tint">
          <HugeiconsIcon icon={Alert01Icon} size={14} className="text-warning dark:text-warning" />
        </div>
        <p className="flex-1 min-w-0 text-sm font-medium text-warning dark:text-warning">
          <span className="font-semibold">{browserName} isn't fully supported.</span>{' '}
          <span className="font-normal">Some features may not work correctly. For the best experience, use Chrome, Safari, Firefox, or Edge.</span>
        </p>
        <button
          onClick={dismiss}
          className="flex-shrink-0 p-1 rounded-full transition-colors hover:bg-warning-tint dark:hover:bg-warning-tint"
          aria-label="Dismiss browser notice"
        >
          <HugeiconsIcon icon={Cancel01Icon} size={14} className="text-warning dark:text-warning" />
        </button>
      </div>
    </>
  )
}
